import React, { useMemo, useState } from "react";
import { Box, Button, IconButton, MenuItem, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { Features, UserRole } from "../../../Enums/companyStatus";
import { VoicesPostState } from "../../../Enums/voices";
import { hasPermission, hasRole } from "../../../Helpers/authHelpers";
import { VoicesCalendarPost } from "../../../Models/voicesCalendar";
import { useVoicesCampaigns } from "../../../Query/useVoicesCampaigns";
import { useMoveVoicesPost, useVoicesCalendar, useVoicesPost } from "../../../Query/useVoicesPosts";
import VoicesCard from "../Common/VoicesCard";
import VoicesInput from "../Common/VoicesInput";
import VoicesPostDialog from "../Posts/VoicesPostDialog";
import VoicesCalendarDay from "./VoicesCalendarDay";
import VoicesCalendarDayView from "./VoicesCalendarDayView";
import { STATE_COLORS, STATE_KEYS } from "./VoicesCalendarPostChip";
import VoicesCalendarTargetFilter from "./VoicesCalendarTargetFilter";
import {
  VoicesCalendarView,
  dayKey,
  movedTime,
  shiftAnchor,
  visibleDays,
} from "./voicesCalendarDates";

const toggleSx = (accent: string) => ({
  "& .MuiToggleButton-root": {
    textTransform: "none",
    color: color.gray700,
    borderColor: color.gray200,
    fontSize: "0.85rem",
    px: 1.5,
  },
  "& .Mui-selected": {
    color: `${accent} !important`,
    backgroundColor: `${color.orange50} !important`,
    fontWeight: 600,
  },
});

const LEGEND_STATES = [
  VoicesPostState.DRAFT,
  VoicesPostState.IN_APPROVAL,
  VoicesPostState.SCHEDULED,
  VoicesPostState.PUBLISHED,
];

const LegendDot: React.FC<{ dot: string; label: string }> = ({ dot, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: dot }} />
    <Typography variant="caption" sx={{ color: color.gray700 }}>
      {label}
    </Typography>
  </Box>
);

const VoicesCalendar: React.FC = () => {
  const { t, i18n } = useTranslation("voicesPosts");
  const { colors: workspace } = useWorkspaceColors();
  const seesEveryone = hasPermission(Features.VOICES_MARKETING) || hasRole(UserRole.ADMIN);

  const [view, setView] = useState<VoicesCalendarView>("month");
  const [anchor, setAnchor] = useState(new Date());
  const [campaignId, setCampaignId] = useState<number | "">("");
  const [target, setTarget] = useState("");
  const [openPostId, setOpenPostId] = useState<number | null>(null);

  const days = useMemo(() => visibleDays(view, anchor), [view, anchor]);
  const from = dayKey(days[0]);
  const to = dayKey(days[days.length - 1]);

  const { data: campaigns = [] } = useVoicesCampaigns();
  const { data: posts = [] } = useVoicesCalendar(from, to, {
    ...(campaignId ? { campaign_id: campaignId } : {}),
    ...(target ? { target } : {}),
  });
  const { data: openPost } = useVoicesPost(openPostId);
  const movePost = useMoveVoicesPost();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const byDay = useMemo(() => {
    const grouped: Record<string, VoicesCalendarPost[]> = {};
    posts.forEach((post) => {
      const key = post.scheduled_at.slice(0, 10);
      grouped[key] = [...(grouped[key] ?? []), post];
    });
    return grouped;
  }, [posts]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const post = posts.find((item) => item.id === active.id);
    if (!post || !over) return;
    const target = String(over.id);
    const toSlot = target.includes("T");
    if (target === post.scheduled_at.slice(0, toSlot ? 16 : 10)) return;
    movePost.mutate({
      postId: post.id,
      scheduledAt: toSlot ? target : movedTime(post.scheduled_at, target),
    });
  };

  const openDay = (day: Date) => {
    setAnchor(day);
    setView("day");
  };

  const title =
    view === "month"
      ? anchor.toLocaleDateString(i18n.language, { month: "long", year: "numeric" })
      : view === "day"
        ? days[0].toLocaleDateString(i18n.language, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : `${days[0].toLocaleDateString(i18n.language, { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <VoicesCard title={t("calendar_title")} subtitle={t("calendar_subtitle")}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
          alignItems: "center",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton size="small" onClick={() => setAnchor(shiftAnchor(view, anchor, -1))}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton size="small" onClick={() => setAnchor(shiftAnchor(view, anchor, 1))}>
            <ChevronRightIcon />
          </IconButton>
          <Button
            size="small"
            onClick={() => setAnchor(new Date())}
            sx={{ textTransform: "none", color: color.gray700 }}
          >
            {t("calendar_today")}
          </Button>
        </Box>
        <Typography
          variant="body1"
          sx={{ fontWeight: 600, textTransform: "capitalize", textAlign: "center", minWidth: 240 }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "flex-start", md: "flex-end" },
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <VoicesInput
            select
            size="small"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value === "" ? "" : Number(e.target.value))}
            sx={{ width: 220 }}
          >
            <MenuItem value="">{t("calendar_allCampaigns")}</MenuItem>
            {campaigns.map((campaign) => (
              <MenuItem key={campaign.id} value={campaign.id}>
                {campaign.name}
              </MenuItem>
            ))}
          </VoicesInput>
          {seesEveryone && <VoicesCalendarTargetFilter value={target} onChange={setTarget} />}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_e, value) => value && setView(value)}
            sx={toggleSx(workspace.accent)}
          >
            <ToggleButton value="day">{t("calendar_day")}</ToggleButton>
            <ToggleButton value="week">{t("calendar_week")}</ToggleButton>
            <ToggleButton value="month">{t("calendar_month")}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        {view === "day" ? (
          <VoicesCalendarDayView
            day={days[0]}
            posts={byDay[dayKey(days[0])] ?? []}
            onOpen={setOpenPostId}
          />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              borderTop: `1px solid ${color.gray200}`,
              borderLeft: `1px solid ${color.gray200}`,
            }}
          >
            {days.slice(0, 7).map((day) => (
              <Typography
                key={`head-${dayKey(day)}`}
                variant="caption"
                sx={{
                  px: 1,
                  py: 0.75,
                  fontWeight: 600,
                  textTransform: "capitalize",
                  borderRight: `1px solid ${color.gray200}`,
                  borderBottom: `1px solid ${color.gray200}`,
                  backgroundColor: color.gray50,
                }}
              >
                {day.toLocaleDateString(i18n.language, { weekday: "short" })}
              </Typography>
            ))}
            {days.map((day) => (
              <VoicesCalendarDay
                key={dayKey(day)}
                day={day}
                posts={byDay[dayKey(day)] ?? []}
                outsideMonth={view === "month" && day.getMonth() !== anchor.getMonth()}
                tall={view === "week"}
                onOpen={setOpenPostId}
                onPickDay={openDay}
              />
            ))}
          </Box>
        )}
      </DndContext>
      <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2, mt: 1.5 }}>
        {LEGEND_STATES.map((state) => (
          <LegendDot key={state} dot={STATE_COLORS[state]} label={t(STATE_KEYS[state])} />
        ))}
        <LegendDot dot={color.red700} label={t("calendar_legendFailed")} />
      </Box>
      <Typography
        variant="caption"
        sx={{ display: "block", textAlign: "center", mt: 0.5, color: color.gray600 }}
      >
        {view === "day" ? t("calendar_dayHint") : t("calendar_dragHint")}
      </Typography>

      <VoicesPostDialog
        open={!!openPost && openPostId !== null}
        campaignId={openPost?.campaign_id ?? null}
        post={openPost ?? null}
        topic={null}
        onClose={() => setOpenPostId(null)}
      />
    </VoicesCard>
  );
};

export default VoicesCalendar;
